/* global axios */
// Integração Wavoip (ligações WhatsApp via webphone). Carrega o SDK UMD do CDN
// uma única vez, registra os tokens da conta e inicia chamadas. Porta do widget
// do AstraChat original (window.wavoip / @wavoip/wavoip-webphone).
const SDK_URL =
  'https://cdn.jsdelivr.net/npm/@wavoip/wavoip-webphone@latest/dist/index.umd.min.js';

let ready = false;
let initInFlight = null;

// ---- histórico: registra a ligação wavoip ao encerrar (p/ o relatório de Ligações) ----
// Saída é rastreada no startCall (temos telefone/direção); entrada tenta ler do evento
// do SDK. Sem telefone não grava (contact_phone é obrigatório no backend).
let currentAccountId = null;
let activeCall = null;

const logWavoipCall = () => {
  const call = activeCall;
  activeCall = null;
  if (!call || !currentAccountId) return;
  const phone = String(call.phone || '').replace(/\D/g, '');
  if (!phone) return;
  const dur = call.answeredAt
    ? Math.max(0, Math.round((Date.now() - call.answeredAt) / 1000))
    : 0;
  axios
    .post(`/api/v1/accounts/${currentAccountId}/kanban/call_history`, {
      contact_phone: phone,
      // conversation_id + provider_call_id permitem o backend ESPELHAR na tabela
      // nativa `calls` e criar a BOLHA de ligação na conversa (não só no relatório).
      conversation_id: call.conversationId || undefined,
      inbox_id: call.inboxId || undefined,
      provider_call_id: call.callId || undefined,
      direction: call.direction || 'OUTGOING',
      status: call.answered ? 'ENDED' : 'NOT_ANSWERED',
      duration_seconds: dur,
      started_at: call.startedAt
        ? new Date(call.startedAt).toISOString()
        : null,
      ended_at: new Date().toISOString(),
      call_method: 'wavoip',
    })
    .catch(() => {});
};

// tenta extrair o telefone do payload do evento do SDK (entrada), com fallbacks.
const evtPhone = evt =>
  (evt &&
    (evt.phone || evt.number || evt.from || evt.tag || evt?.call?.phone)) ||
  '';

const loadScript = () =>
  new Promise((resolve, reject) => {
    if (window.wavoipWebphone) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });

const fetchTokens = async accountId => {
  const { data } = await axios.get(
    `/api/v1/accounts/${accountId}/kanban/wavoip_tokens/all`
  );
  return data.data || [];
};

const initWavoip = async accountId => {
  if (ready) return true;
  if (initInFlight) return initInFlight;
  initInFlight = (async () => {
    const tokens = await fetchTokens(accountId);
    if (!tokens.length) return false;
    await loadScript();
    const api = await window.wavoipWebphone.render({
      theme: 'system',
      position: 'bottom-right',
      widget: { startOpen: false, showWidgetButton: false },
      callSettings: { displayName: 'Atendimento' },
      // Identificador da plataforma hospedeira repassado ao Wavoip em TODA
      // ligação — assim o time do Wavoip sabe que o tráfego vem do AstraChat.
      platform: 'ASTRAONLINE',
    });
    tokens.forEach(t => api.device.add(t, true));
    // ENTRADA: oferta recebida (toque). Registra a ligação PENDENTE já aqui, p/
    // capturar também a PERDIDA (oferta que termina sem ser aceita). Não
    // sobrescreve uma ligação de SAÍDA em andamento.
    api.on('offer:received', offer => {
      const phone = String(
        (offer && (offer?.peer?.phone || offer.phone || offer.number || offer.tag)) ||
          ''
      ).replace(/\D/g, '');
      if (!phone) return;
      if (activeCall && activeCall.direction === 'OUTGOING') return;
      activeCall = {
        phone,
        direction: 'INCOMING',
        callId: (offer && (offer.id || offer?.call?.id)) || null,
        startedAt: Date.now(),
        answered: false,
      };
    });
    api.on('call:accepted', evt => {
      // entrada: o SDK não passou por startCall → cria o registro a partir do evento
      if (!activeCall) {
        activeCall = {
          phone: evtPhone(evt),
          direction: 'INCOMING',
          startedAt: Date.now(),
        };
      }
      activeCall.answered = true;
      activeCall.answeredAt = Date.now();
      if (!activeCall.callId) {
        activeCall.callId = (evt && (evt.id || evt?.call?.id)) || null;
      }
      try {
        api.widget.open();
      } catch (e) {
        /* noop */
      }
    });
    api.on('call:ended', () => {
      logWavoipCall();
      // NÃO fecha o widget na hora: numa chamada de SAÍDA que encerra rápido
      // (ex.: número recusado), fechar no ato fazia o widget "abrir e sumir", sem
      // o atendente ver o desfecho. Espera alguns segundos p/ ele ver o estado
      // (encerrada/recusada) e só então minimiza.
      setTimeout(() => {
        try {
          api.widget.close();
        } catch (e) {
          /* noop */
        }
      }, 4000);
    });
    ready = true;
    return true;
  })();
  try {
    return await initInFlight;
  } finally {
    initInFlight = null;
  }
};

// O device.add() conecta o websocket de forma ASSÍNCRONA; o device só ganha
// status 'open' (e enable=true, exigido p/ ORIGINAR) depois disso. Discar logo
// após o add — como acontecia na 1ª ligação — deixava a saída SEM device de
// origem: a chamada nascia e caía na hora ("chama e já desliga"). A entrada não
// sofria porque, quando chega uma ligação, o device já está 'open' há um tempo.
// Aqui esperamos algum device abrir (poll curto) antes de discar.
const waitForOpenDeviceTokens = async (timeoutMs = 8000) => {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const devs = window.wavoip?.device?.get?.() || [];
    const open = devs.filter(d => d.status === 'open').map(d => d.token);
    if (open.length) return open;
    if (Date.now() >= deadline) return devs.map(d => d.token); // fallback: tenta com o que houver
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => {
      setTimeout(resolve, 250);
    });
  }
};

// Normaliza número BR para o formato que o WhatsApp/Wavoip espera: com DDI 55 e
// o 9º dígito nos celulares. Muitos contatos antigos vêm SEM o 9 (ex.:
// 553197571759 = 55 + DDD + 8 dígitos) e o Wavoip recusa na hora — a chamada de
// saída "nasce e cai" e o widget some. Aqui só mexemos em número BR reconhecível.
const normalizeBrPhone = raw => {
  let d = String(raw || '').replace(/\D/g, '');
  if (!d) return d;
  // sem DDI (DDD + número): prefixa 55
  if ((d.length === 10 || d.length === 11) && !d.startsWith('55')) d = `55${d}`;
  // com DDI e 12 dígitos (55 + DDD + 8) = falta o 9 do celular -> insere
  if (d.length === 12 && d.startsWith('55') && '6789'.includes(d[4])) {
    d = `${d.slice(0, 4)}9${d.slice(4)}`;
  }
  return d;
};

export function useWavoip() {
  const startCall = async (accountId, phone, opts2 = {}) => {
    const ok = await initWavoip(accountId);
    if (!ok || !window.wavoip) {
      throw new Error('wavoip-not-ready');
    }
    const digits = normalizeBrPhone(phone);
    if (!digits) throw new Error('no-phone');
    // rastreia a ligação de SAÍDA p/ registrar no histórico ao encerrar +
    // conversationId/inboxId p/ registrar a BOLHA de ligação na conversa.
    currentAccountId = accountId;
    activeCall = {
      phone: digits,
      direction: 'OUTGOING',
      startedAt: Date.now(),
      conversationId: opts2.conversationId,
      inboxId: opts2.inboxId,
    };
    // garante um device conectado antes de discar (ver comentário acima)
    const fromTokens = await waitForOpenDeviceTokens();
    try {
      window.wavoip.widget.open();
    } catch (e) {
      /* noop */
    }
    const opts = { displayName: 'Atendimento' };
    if (fromTokens.length) opts.fromTokens = fromTokens;
    // call.start RESOLVE com { call, err } (não lança). Antes o retorno era
    // ignorado e a falha ficava SILENCIOSA. Surfaça o motivo (err.devices[].reason)
    // p/ o chamador tratar/mostrar.
    const res = await window.wavoip.call.start(digits, opts);
    if (res && res.err) {
      activeCall = null;
      const reason =
        (res.err.devices || [])
          .map(d => d.reason)
          .filter(Boolean)
          .join(', ') || res.err.message;
      throw new Error(reason || 'wavoip-call-failed');
    }
    // guarda o id da chamada (provider_call_id) p/ o backend espelhar e criar a
    // bolha na conversa ao encerrar.
    if (res && res.call && res.call.id && activeCall) {
      activeCall.callId = res.call.id;
    }
    return res;
  };

  // Registra o device (abre o WebSocket + handlers de ENTRADA) SEM discar. Usado
  // ao abrir a página/conversa de uma caixa Wavoip: assim a atendente já fica
  // disponível p/ RECEBER ligação, em vez do WebSocket só abrir quando ela FAZ uma
  // ligação (antes, quem só recebia nunca registrava o device). Idempotente.
  const init = accountId => initWavoip(accountId).catch(() => false);

  return { startCall, init };
}
